import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { map, Observable, of, Subject, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { apiUrl } from './api-url';

// ── PKCE helper ───────────────────────────────────────────────────────────────

function base64UrlEncode(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// ── Response shapes from the backend (Problem wrapper) ────────────────────

interface ProblemResponse<T> {
  status: number;
  data: T;
}

export interface EmailAuthResponse {
  instructions: string;
  email: string;
}

// totp is populated only when the user needs to set up 2FA for the first time.
// session is populated when 2FA is disabled (FEATURE_FLAG_2FA=none) - the user is logged in directly.
// hasTotp/hasPasskey indicate which 2FA methods the user has configured (when passkey flag is enabled).
export interface EmailCodeResponse {
  totp?: string;
  session?: UserSession;
  hasTotp: boolean;
  hasPasskey: boolean;
}

export interface UserSession {
  id: string;
  userID: string;
  createdAt: string;
  expiresAt: string;
}

export interface BoMemberInfo {
  isMember: boolean;
  memberId?: string;
  name?: string;
  isSuperAdmin: boolean;
  assignments?: { module: string; role: string }[];
  pendingCount?: number;
  orgsPendingCount?: number;
  paymentGatewaysPendingCount?: number;
  currencySettingsPendingCount?: number;
  customFeesPendingCount?: number;
  paymentMethodsPendingCount?: number;
  manualSettlementPendingCount?: number;
  marketsPendingCount?: number;
  processCodesPendingCount?: number;
  boDepositsPendingCount?: number;
  boWithdrawalsPendingCount?: number;
  depositExceptionPendingCount?: number;
  withdrawalExceptionPendingCount?: number;
  emailNotificationsEnabled?: boolean;
  orgTeamsPendingCount?: number;
  boApiKeysPendingCount?: number;
  kycPendingCount?: number;
  kycRequirementsPendingCount?: number;
  financialInstitutionsPendingCount?: number;
  downloadCenterPendingCount?: number;
  retailRemittancePendingCount?: number;
  canAccessSystemMonitor?: boolean;
  canPerformSystemMonitorAdminOps?: boolean;
  systemMonitorTabs?: string[];
  announcementsPendingCount?: number;
  otcCorridorPendingCount?: number;
  indicativeInterventionsPendingCount?: number;
}

export interface OrgMemberInfo {
  isMember: boolean;
  memberId?: string;
  name?: string;
  orgId?: string;
  orgName?: string;
  orgDomain?: string;
  isSuperAdmin: boolean;
  assignments?: { module: string; role: string }[];
  pendingCount?: number;
  orgWithdrawalsPendingCount?: number;
  orgInstantOffersPendingCount?: number;
  orgIndicativeOffersPendingCount?: number;
  orgEasyConvertPendingCount?: number;
  orgIndicativeOffersOpenBidsCount?: number;
  orgOngoingSettlementsCount?: number;
  orgIndicativeMarketNewCount?: number;
  orgApiKeysPendingCount?: number;
  otcRefundsPendingCount?: number;
  emailNotificationsEnabled?: boolean;
  orgCwrEnabled?: boolean;
  orgKycStatus?: string;
  orgAllowExternalMembers?: boolean;
  orgApiAccessEnabled?: boolean;
  orgRoles?: string[];
}

export interface WhoAmIResult {
  email: string;
  userRoles: { isAdmin: boolean };
  backoffice?: BoMemberInfo;
  orgTeam?: OrgMemberInfo;
  orgTeams?: OrgMemberInfo[];
  /** Why the user has no active org (blocked-state copy): 'org_disabled' | 'member_suspended' | 'no_membership'. */
  noActiveOrgReason?: 'org_disabled' | 'member_suspended' | 'no_membership';
  activeContext?: string;
  hasPasskey?: boolean;
  totpEnabled?: boolean;
  memberSince?: string;
  privacyPolicyAcceptedAt?: string | null;
  preferredCurrencies?: string[];
  inboxUnreadCount?: number;
}

// ── Storage keys ───────────────────────────────────────────────────────────

const SESSION_KEY = 'th_session';
const IMPERSONATION_KEY = 'th_impersonation';
const KC_REFRESH_KEY = 'th_kc_refresh';
const KC_ID_TOKEN_KEY = 'th_kc_id_token';

// Shape returned by the backend KC callback and refresh endpoints.
interface KcTokenResponse {
  accessToken?: string;
  refreshToken?: string;
  expiresAt: string;
  email?: string;
  idToken?: string;
}

// ── Service ───────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private base = environment.apiBaseUrl;

  /** Emit to trigger a re-fetch of the whoAmI user data (e.g. after pending count changes). */
  refreshUser$ = new Subject<void>();

  /** Current authenticated user — set by DashboardComponent after every whoAmI fetch. */
  currentUser = signal<WhoAmIResult | null>(null);

  /** The org ID the user has currently selected (multi-org). Derived from the backend-persisted ActiveOrgID. */
  activeOrgId = signal<string | null>(null);

  /** The OrgMemberInfo for the currently active org. Falls back to orgTeams[0] then orgTeam. */
  activeOrgTeam = computed(() => {
    const user = this.currentUser();
    const id = this.activeOrgId();
    return (
      user?.orgTeams?.find((o) => o.orgId === id) ?? user?.orgTeams?.[0] ?? user?.orgTeam ?? null
    );
  });

  /**
   * Whether the active ORG member holds one of `roles` on `module` (use on org-context
   * pages). Super admin (legacy) passes. Mirrors the backend org HasRole gate. Signal-based,
   * so it is reactive inside component computeds.
   */
  hasOrgModuleRole(module: string, ...roles: string[]): boolean {
    const org = this.activeOrgTeam();
    if (!org?.isMember) return false;
    if (org.isSuperAdmin) return true;
    return (org.assignments ?? []).some((a) => a.module === module && roles.includes(a.role));
  }

  /**
   * Whether the current user holds one of `roles` on a BACKOFFICE `module` (use on
   * backoffice-context pages). Super admin (legacy) passes. Mirrors the backend backoffice
   * HasRole gate.
   */
  hasBackofficeModuleRole(module: string, ...roles: string[]): boolean {
    const bo = this.currentUser()?.backoffice;
    if (!bo?.isMember) return false;
    if (bo.isSuperAdmin) return true;
    return (bo.assignments ?? []).some((a) => a.module === module && roles.includes(a.role));
  }

  /** Whether an admin is currently viewing as another user. Persisted to localStorage (token stays in HttpOnly cookie). */
  private _isImpersonating = signal<boolean>(this._loadImpersonation()?.isActive ?? false);
  private _impersonationTarget = signal<string | null>(
    this._loadImpersonation()?.targetEmail ?? null,
  );
  private _impersonationName = signal<string | null>(this._loadImpersonation()?.targetName ?? null);
  private _impersonationOrg = signal<string | null>(this._loadImpersonation()?.targetOrg ?? null);

  readonly isImpersonating = this._isImpersonating.asReadonly();
  readonly impersonationTarget = this._impersonationTarget.asReadonly();
  readonly impersonationName = this._impersonationName.asReadonly();
  readonly impersonationOrg = this._impersonationOrg.asReadonly();

  private _loadImpersonation(): {
    isActive: boolean;
    targetEmail: string;
    targetName?: string;
    targetOrg?: string;
  } | null {
    try {
      const raw = localStorage.getItem(IMPERSONATION_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  setImpersonationToken(targetEmail: string, targetName?: string, targetOrg?: string): void {
    localStorage.setItem(
      IMPERSONATION_KEY,
      JSON.stringify({ isActive: true, targetEmail, targetName, targetOrg }),
    );
    this._isImpersonating.set(true);
    this._impersonationTarget.set(targetEmail);
    this._impersonationName.set(targetName ?? null);
    this._impersonationOrg.set(targetOrg ?? null);
  }

  clearImpersonation(): void {
    localStorage.removeItem(IMPERSONATION_KEY);
    this._isImpersonating.set(false);
    this._impersonationTarget.set(null);
    this._impersonationName.set(null);
    this._impersonationOrg.set(null);
  }

  /** Briefly true when a write is blocked by the impersonation read-only guard — used to show a toast. */
  readonly readOnlyBlocked = signal(false);
  private _readOnlyTimer: ReturnType<typeof setTimeout> | null = null;

  showReadOnlyAlert(): void {
    if (this._readOnlyTimer) clearTimeout(this._readOnlyTimer);
    this.readOnlyBlocked.set(true);
    this._readOnlyTimer = setTimeout(() => this.readOnlyBlocked.set(false), 3000);
  }

  /** Revokes the impersonation session on the backend (clears the HttpOnly cookie) then clears local state. */
  endImpersonationOnBackend(): void {
    this.http
      .delete<unknown>(`${this.base}/backoffice/impersonation`, { withCredentials: true })
      .pipe(take(1))
      .subscribe({
        complete: () => this.clearImpersonation(),
        error: () => this.clearImpersonation(),
      });
  }

  // ── Persistence ──────────────────────────────────────────────────────────

  saveSession(session: UserSession): void {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }

  getSession(): UserSession | null {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    try {
      const session = JSON.parse(raw) as UserSession;
      if (new Date(session.expiresAt) <= new Date()) {
        this.clearSession();
        return null;
      }
      return session;
    } catch {
      this.clearSession();
      return null;
    }
  }

  clearSession(): void {
    if (this.kcRefreshTimer !== null) {
      clearTimeout(this.kcRefreshTimer);
      this.kcRefreshTimer = null;
    }
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(KC_REFRESH_KEY);
    localStorage.removeItem(KC_ID_TOKEN_KEY);
  }

  /** Returns the stored KC id_token, used as id_token_hint on logout. */
  getKcIdToken(): string | null {
    return localStorage.getItem(KC_ID_TOKEN_KEY);
  }

  isLoggedIn(): boolean {
    const session = this.getSession();
    if (!session) return false;
    return new Date(session.expiresAt) > new Date();
  }

  // ── API calls ────────────────────────────────────────────────────────────

  sendCode(email: string): Observable<EmailAuthResponse> {
    return this.http
      .post<ProblemResponse<EmailAuthResponse>>(`${this.base}/auth/email/send`, { email })
      .pipe(map((r) => r.data));
  }

  verifyCode(email: string, verificationCode: string): Observable<EmailCodeResponse> {
    return this.http
      .post<ProblemResponse<EmailCodeResponse>>(`${this.base}/auth/email/verify`, {
        email,
        verificationCode,
      })
      .pipe(map((r) => r.data));
  }

  confirmTotp(email: string, code: string): Observable<UserSession> {
    return this.http
      .post<ProblemResponse<UserSession>>(`${this.base}/auth/totp/confirm`, { email, code })
      .pipe(map((r) => r.data));
  }

  setupTotp(email: string): Observable<{ totp: string }> {
    return this.http
      .post<ProblemResponse<{ totp: string }>>(`${this.base}/auth/totp/setup`, { email })
      .pipe(map((r) => r.data));
  }

  passkeyRegisterBegin(email: string): Observable<any> {
    return this.http
      .post<ProblemResponse<any>>(`${this.base}/auth/passkey/register/begin`, { email })
      .pipe(map((r) => r.data));
  }

  passkeyRegisterFinish(email: string, credential: any): Observable<UserSession> {
    return this.http
      .post<
        ProblemResponse<UserSession>
      >(`${this.base}/auth/passkey/register/finish?email=${encodeURIComponent(email)}`, credential)
      .pipe(map((r) => r.data));
  }

  passkeyAuthBegin(email: string): Observable<any> {
    return this.http
      .post<ProblemResponse<any>>(`${this.base}/auth/passkey/authenticate/begin`, { email })
      .pipe(map((r) => r.data));
  }

  passkeyAuthFinish(email: string, credential: any): Observable<UserSession> {
    return this.http
      .post<
        ProblemResponse<UserSession>
      >(`${this.base}/auth/passkey/authenticate/finish?email=${encodeURIComponent(email)}`, credential)
      .pipe(map((r) => r.data));
  }

  passkeyDiscoverBegin(): Observable<{ options: any; sessionKey: string }> {
    return this.http
      .post<
        ProblemResponse<{ options: any; sessionKey: string }>
      >(`${this.base}/auth/passkey/discover/begin`, {})
      .pipe(map((r) => r.data));
  }

  passkeyDiscoverFinish(sessionKey: string, credential: any): Observable<UserSession> {
    return this.http
      .post<
        ProblemResponse<UserSession>
      >(`${this.base}/auth/passkey/discover/finish?key=${encodeURIComponent(sessionKey)}`, credential)
      .pipe(map((r) => r.data));
  }

  whoAmI(sessionId: string): Observable<WhoAmIResult> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${sessionId}` });
    return this.http
      .get<ProblemResponse<WhoAmIResult>>(`${this.base}/users/me`, { headers })
      .pipe(map((r) => r.data));
  }

  /** Persists the user's preferred display currencies. */
  patchPreferences(preferredCurrencies: string[]): Observable<void> {
    const session = this.getSession();
    if (!session?.id) return of(void 0);
    const headers = new HttpHeaders({ Authorization: `Bearer ${session.id}` });
    return this.http
      .patch<unknown>(
        `${this.base}/users/me/preferences`,
        { preferred_currencies: preferredCurrencies },
        { headers },
      )
      .pipe(map(() => void 0));
  }

  /** Switches the active org locally and persists the choice to the backend. */
  setActiveOrg(orgId: string): Observable<void> {
    this.activeOrgId.set(orgId);
    const session = this.getSession();
    if (!session?.id) return of(void 0);
    const headers = new HttpHeaders({ Authorization: `Bearer ${session.id}` });
    return this.http
      .put<unknown>(`${this.base}/users/me/active-org`, { orgId }, { headers })
      .pipe(map(() => void 0));
  }

  /** Persists the active context (org/backoffice) to the backend (fire-and-forget). */
  setActiveContext(context: 'org' | 'backoffice'): void {
    const session = this.getSession();
    if (!session?.id) return;
    const headers = new HttpHeaders({ Authorization: `Bearer ${session.id}` });
    this.http
      .put<unknown>(`${this.base}/users/me/active-org`, { context }, { headers })
      .subscribe();
  }

  logout(sessionId: string): Observable<void> {
    // KC mode: session.id is a KC JWT. Clear HttpOnly cookies via backend logout endpoint.
    if (sessionId && !sessionId.startsWith('SESSIONID_')) {
      return this.http
        .post<unknown>(apiUrl('auth/keycloak/logout'), {}, { withCredentials: true })
        .pipe(map(() => void 0));
    }
    const headers = new HttpHeaders({ Authorization: `Bearer ${sessionId}` });
    return this.http
      .post<ProblemResponse<unknown>>(`${this.base}/auth/logout`, {}, { headers })
      .pipe(map(() => void 0));
  }

  private kcRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Schedules a silent KC token refresh 90 seconds before the current access
   * token expires. On success the new token is saved and the next refresh is
   * rescheduled automatically. Safe to call multiple times — cancels any
   * previously scheduled timer first.
   * No-op when not in KC mode (no refresh token in storage).
   */
  scheduleKcTokenRefresh(): void {
    if (this.kcRefreshTimer !== null) {
      clearTimeout(this.kcRefreshTimer);
      this.kcRefreshTimer = null;
    }
    const session = this.getSession();
    if (!session) return;
    // KC mode: session.id is a KC JWT (not a SESSIONID_ internal session token).
    const isKcMode = !session.id.startsWith('SESSIONID_');
    if (!isKcMode) return;

    const expiresMs = new Date(session.expiresAt).getTime();
    const delay = Math.max(5_000, expiresMs - Date.now() - 90_000);

    this.kcRefreshTimer = setTimeout(() => {
      this.kcRefreshTimer = null;
      const current = this.getSession();
      if (!current) return;
      this.extendSession(current.id)
        .pipe(take(1))
        .subscribe({
          next: () => this.scheduleKcTokenRefresh(),
          error: () => this.clearSession(),
        });
    }, delay);
  }

  extendSession(sessionId: string): Observable<string> {
    // KC mode: session.id is a JWT (not a SESSIONID_ internal session token).
    // Refresh token lives only in the HttpOnly kc_refresh_token cookie — send empty body.
    const isKcMode = !sessionId.startsWith('SESSIONID_');
    if (isKcMode) {
      return this.http.post<{ data: KcTokenResponse }>(apiUrl('auth/keycloak/refresh'), {}).pipe(
        map((r) => {
          const session = this.getSession();
          if (session) {
            this.saveSession({
              ...session,
              id: r.data.accessToken ?? session.id,
              expiresAt: r.data.expiresAt,
            });
          }
          return r.data.expiresAt;
        }),
      );
    }
    const headers = new HttpHeaders({ Authorization: `Bearer ${sessionId}` });
    return this.http
      .put<
        ProblemResponse<{ expiresAt: string }>
      >(`${this.base}/auth/session/extend`, {}, { headers })
      .pipe(
        map((r) => {
          const newExpiry = r.data.expiresAt;
          const session = this.getSession();
          if (session) this.saveSession({ ...session, expiresAt: newExpiry });
          return newExpiry;
        }),
      );
  }

  // ── Keycloak PKCE ─────────────────────────────────────────────────────────

  async generatePkcePair(): Promise<{ verifier: string; challenge: string }> {
    const buf = new Uint8Array(32);
    crypto.getRandomValues(buf);
    const verifier = base64UrlEncode(buf);
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    const challenge = base64UrlEncode(new Uint8Array(digest));
    return { verifier, challenge };
  }

  keycloakCallback(input: {
    code: string;
    codeVerifier: string;
    redirectUri: string;
  }): Observable<UserSession> {
    return this.http.post<{ data: KcTokenResponse }>(apiUrl('auth/keycloak/callback'), input).pipe(
      map((r) => {
        // id_token stored for use as id_token_hint on logout.
        // Refresh token is in HttpOnly cookie only — not stored in localStorage.
        if (r.data.idToken) {
          localStorage.setItem(KC_ID_TOKEN_KEY, r.data.idToken);
        }
        return {
          id: r.data.accessToken ?? '',
          userID: r.data.email ?? '',
          createdAt: new Date().toISOString(),
          expiresAt: r.data.expiresAt,
        };
      }),
    );
  }

  reauthKeycloakBegin(): Observable<{ auth_url: string; state: string }> {
    return this.http
      .post<
        ProblemResponse<{ auth_url: string; state: string }>
      >(apiUrl('auth/reauth/keycloak/begin'), {})
      .pipe(map((r) => r.data));
  }

  reauthKeycloakFinish(body: {
    code: string;
    state: string;
    codeVerifier: string;
    redirectUri: string;
  }): Observable<{ token: string; expiresAt: string }> {
    return this.http
      .post<
        ProblemResponse<{ token: string; expiresAt: string }>
      >(apiUrl('auth/reauth/keycloak/finish'), body)
      .pipe(map((r) => r.data));
  }
}
